import type {
  AuditResponse,
  AuthConfigResponse,
  CheckRunsResponse,
  HistoryRange,
  HistoryResponse,
  HubUser,
  IncidentsResponse,
  InventoryResponse,
  MaintenanceInput,
  MaintenanceResponse,
  MeResponse,
  MetricRange,
  MetricSeriesResponse,
  MonthlyReportResponse,
  ProblemsResponse,
  QuietWindow,
  StatsResponse,
  StatusResponse,
  TestNotificationResponse,
  UserInput,
  UsersResponse,
} from "@central-hub/contracts";

const TIMEOUT_MS = 10_000;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: Record<string, unknown> | null,
  ) {
    super(typeof body?.error === "string" ? body.error : `HTTP ${status}`);
  }
}

async function request<T>(path: string, token: string | null, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      accept: "application/json",
      ...(typeof init.body === "string" && { "content-type": "application/json" }),
      ...(token && { authorization: `Bearer ${token}` }),
    },
    signal: AbortSignal.timeout(TIMEOUT_MS),
  });
  const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (!response.ok) throw new ApiError(response.status, body);
  return body as T;
}

export const api = {
  authConfig: () => request<AuthConfigResponse>("/api/v1/auth/config", null),
  me: (token: string) => request<MeResponse>("/api/v1/me", token),
  status: (token: string) => request<StatusResponse>("/api/v1/status", token),
  openIncidents: (token: string) =>
    request<IncidentsResponse>("/api/v1/incidents?status=open&limit=500", token),
  resolvedIncidents: (token: string) =>
    request<IncidentsResponse>("/api/v1/incidents?status=resolved&limit=50", token),
  stats: (token: string) => request<StatsResponse>("/api/v1/stats", token),
  history: (token: string, target: string, check: string, range: HistoryRange) =>
    request<HistoryResponse>(
      `/api/v1/history?${new URLSearchParams({ target, check, range })}`,
      token,
    ),
  targetIncidents: (token: string, target: string) =>
    request<IncidentsResponse>(
      `/api/v1/incidents?${new URLSearchParams({ target, limit: "50" })}`,
      token,
    ),
  problems: (token: string, target: string, check: string, from: number, to: number) =>
    request<ProblemsResponse>(
      `/api/v1/problems?${new URLSearchParams({
        target,
        check,
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
      })}`,
      token,
    ),
  runs: (token: string, target: string, check: string) =>
    request<CheckRunsResponse>(`/api/v1/runs?${new URLSearchParams({ target, check })}`, token),
  /** The latest runs of every deploy workflow (checks the github module gave a deployUrl). */
  recentDeploys: (token: string, limit: number) =>
    request<CheckRunsResponse>(
      `/api/v1/runs?${new URLSearchParams({ having: "deployUrl", limit: String(limit) })}`,
      token,
    ),
  metricSeries: (token: string, name: string, range: MetricRange) =>
    request<MetricSeriesResponse>(
      `/api/v1/metrics/series?${new URLSearchParams({ name, range })}`,
      token,
    ),
  testNotification: (token: string) =>
    request<TestNotificationResponse>("/api/v1/notifications/test", token, { method: "POST" }),
  /** A module's live lookup; `target` for one about a target. */
  lookup: <T>(token: string, module: string, id: string, params: Record<string, string> = {}) =>
    request<T>(`/api/v1/lookup/${module}/${id}?${new URLSearchParams(params)}`, token),
  users: (token: string) => request<UsersResponse>("/api/v1/users", token),
  maintenance: (token: string) => request<MaintenanceResponse>("/api/v1/maintenance", token),
  inventory: (token: string) => request<InventoryResponse>("/api/v1/inventory", token),
  report: (token: string, target: string, month: string) =>
    request<MonthlyReportResponse>(
      `/api/v1/reports/${encodeURIComponent(target)}?month=${encodeURIComponent(month)}`,
      token,
    ),
  openMaintenance: (token: string, input: MaintenanceInput) =>
    request<QuietWindow>("/api/v1/maintenance", token, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  endMaintenance: (token: string, id: number) =>
    request<null>(`/api/v1/maintenance/${id}`, token, { method: "DELETE" }),
  audit: (token: string, limit = 100) =>
    request<AuditResponse>(`/api/v1/audit?limit=${limit}`, token),
  saveUser: (token: string, email: string, input: UserInput) =>
    request<HubUser>(`/api/v1/users/${encodeURIComponent(email)}`, token, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  removeUser: (token: string, email: string) =>
    request<null>(`/api/v1/users/${encodeURIComponent(email)}`, token, { method: "DELETE" }),
};
